import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Chip, Tooltip } from '@mui/material';
import { OpenInNew, AutoAwesome } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { useSocketEvent } from '../hooks/useSocketEvent';
import { requestGetSuggestions } from '../redux/sagas/requests/ai';
import { formatTimeAgo } from '../utils/dateUtils';
import { actionLabel, confidenceLabel, targetDisplayName } from '../utils/suggestionUtils';

const Row = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: theme.spacing(0.75),
    borderRadius: theme.shape.borderRadius,
    borderLeft: `2px solid ${theme.palette.warning.main}`,
    cursor: 'pointer',
    marginBottom: theme.spacing(0.5),
    '&:hover': { background: theme.palette.action.hover },
}));

const RowTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.smd,
    fontWeight: theme.typography.fontWeightMedium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const RowMeta = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const RowChips = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: 2,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3, 1),
    color: theme.palette.text.secondary,
    gap: theme.spacing(0.5),
}));

const ViewAllRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(1),
}));

const ViewAllButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontSize: theme.typography.size.sm,
}));

const ViewAllIcon = styled(OpenInNew)(({ theme }) => ({
    fontSize: `${theme.typography.size.md} !important`,
}));

const MoreNote = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.xs,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
}));

const EmptyIcon = styled(AutoAwesome)(({ theme }) => ({
    color: theme.palette.text.disabled,
}));

const WIDGET_LIMIT = 5;

const PendingSuggestionsWidget = ({ widgetId, config = {}, onRemove }) => {
    const navigate = useNavigate();
    const suggestionsRefresh = useSelector((state) => state.refreshTriggers.suggestions);

    const fetchConfig = useMemo(
        () => ({
            apiCall: () => requestGetSuggestions({ scope: 'mine', status: 'pending', limit: WIDGET_LIMIT }),
            cacheKey: 'pending-suggestions-mine',
            transform: (response) => response,
        }),
        []
    );

    const { data, isLoading, error, refresh, mutate } = useFetchQueue(widgetId, fetchConfig);

    // Skip the mount-time pass, which would only bust the fetch cache to re-read what it just read.
    const lastRefreshRef = useRef(suggestionsRefresh);
    useEffect(() => {
        if (lastRefreshRef.current !== suggestionsRefresh) {
            lastRefreshRef.current = suggestionsRefresh;
            refresh();
        }
    }, [suggestionsRefresh, refresh]);

    useSocketEvent('suggestion:new', (row) => {
        if (!row?._id || row.status !== 'pending') return;
        mutate((prev) => {
            const list = (prev?.items || []).filter((item) => item._id !== row._id);
            return { items: [row, ...list].slice(0, WIDGET_LIMIT), total: (prev?.total || 0) + 1 };
        });
    });

    useSocketEvent('suggestion:updated', (row) => {
        if (!row?._id) return;
        mutate((prev) => {
            const list = prev?.items || [];
            if (row.status === 'pending') {
                return { items: list.map((item) => (item._id === row._id ? row : item)), total: prev?.total || 0 };
            }
            const next = list.filter((item) => item._id !== row._id);
            if (next.length === list.length) return prev;
            return { items: next, total: Math.max((prev?.total || 1) - 1, 0) };
        });
    });

    const items = data?.items || [];
    const total = data?.total || 0;
    const hidden = Math.max(total - items.length, 0);

    return (
        <BaseWidget title="Suggested Actions" widgetId={widgetId} onRemove={onRemove} isLoading={isLoading} error={error}>
            {items.length === 0 && !isLoading ? (
                <EmptyState>
                    <EmptyIcon />
                    <Typography variant="body2">Nothing to review</Typography>
                    <Typography variant="caption">The assistant has no suggestions waiting for you.</Typography>
                </EmptyState>
            ) : (
                <>
                    {items.map((item) => {
                        const confidence = confidenceLabel(item.ai?.score);
                        const targetName = targetDisplayName(item.target);
                        return (
                            <Tooltip key={item._id} title="Open this suggestion to see exactly what it would do">
                                <Row onClick={() => navigate(`/ai-actions?suggestion=${item._id}`)}>
                                    <RowTitle>{item.ai?.title || actionLabel(item.actionType)}</RowTitle>
                                    <RowMeta>
                                        {actionLabel(item.actionType)}
                                        {targetName ? ` · ${targetName}` : ''}
                                        {item.createdAt ? ` · ${formatTimeAgo(item.createdAt)}` : ''}
                                    </RowMeta>
                                    {confidence && (
                                        <RowChips>
                                            <Chip label={confidence.text} size="small" color={confidence.color} variant="outlined" />
                                        </RowChips>
                                    )}
                                </Row>
                            </Tooltip>
                        );
                    })}
                    {hidden > 0 && <MoreNote>and {hidden} more waiting for review</MoreNote>}
                    <ViewAllRow>
                        <ViewAllButton size="small" endIcon={<ViewAllIcon />} onClick={() => navigate('/ai-actions')}>
                            View all
                        </ViewAllButton>
                    </ViewAllRow>
                </>
            )}
        </BaseWidget>
    );
};

export default PendingSuggestionsWidget;
